<template>
  <BaseWidget
    :deviceIds="deviceIds"
    :widgetId="widgetId"
    :widgetType="widgetType"
    :widgetMaxDevices="widgetMaxDevices"
    :title="name"
    :isGrayOut="isWaitingInitialData(deviceId)"
    :subtitle="deviceType"
    :enableDisconnectedDeviceTheme="
      !isDeviceConnected(deviceId) || !isSmartBridgeConnected(deviceId)
    "
    :isDialog="isDialog"
    :showFooter="!isLoading"
    @close="close"
    @removeWidget="removeWidget"
    @refreshDashboard="refreshDashboard"
    :class="hasLatestStudy ? 'study-widget' : 'continuous-monitoring-widget'"
    :data-cy="hasLatestStudy ? 'studyWidget' : 'continuousMonitoringWidget'"
  >
    <template v-slot:actions>
      <MeasurementOptions
        :deviceIds="deviceIds"
        :passedinParameter="selectedParameter"
        :widgetId="widgetId"
        :filterMeasurementStatistics="true"
        @parameterChange="parameterChange"
      />
      <v-divider v-if="!isDialog" />
    </template>
    <template v-slot:toolbar>
      <PopoverToolbar :deviceId="deviceId" />
    </template>
    <template v-slot:left-toolbar>
      <div class="toolbar">
        <v-btn variant="text" @click="refresh" data-cy="refreshPopover">
          <v-icon icon="mdi-refresh" color="grey" />
        </v-btn>
      </div>
    </template>
    <template v-slot:content>
      <LoadingContainer v-if="isLoading" />
      <StudyContent
        v-if="hasLatestStudy && !isLoading"
        :deviceId="deviceId"
        :selectedParameter="selectedParameter"
      />
      <ContinuousMonitoringContent
        v-else-if="!isLoading"
        :deviceId="deviceId"
        :selectedParameter="selectedParameter"
      />
      <WaitingInitialData
        className="smart-bridge-card "
        v-if="isWaitingInitialData(deviceId) && !isLoading"
      />
    </template>
    <template v-slot:footer>
      <ContinuousMonitoringAndStudyFooter v-if="!isLoading" :deviceId="deviceId" />
    </template>
  </BaseWidget>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, type PropType } from 'vue'
import { useStore } from 'vuex'
import BaseWidget from '@/components/widgets/BaseWidget.vue'
import StudyContent from '@/components/devicePopover/layouts/StudyContent.vue'
import ContinuousMonitoringContent from '@/components/devicePopover/layouts/ContinuousMonitoringContent.vue'
import ContinuousMonitoringAndStudyFooter from '@/components/devicePopover/layouts/ContinuousMonitoringAndStudyFooter.vue'
import PopoverToolbar from '@/components/devicePopover/content/title/popoverToolbar.vue'
import MeasurementOptions from '@/components/devicePopover/content/dropdown/MeasurementOptions.vue'
import LoadingContainer from '@/components/devicePopover/layouts/LoadingContainer.vue'
import WaitingInitialData from '@/components/widgets/currentReadings/content/WaitingInitialData.vue'
import { deviceModelDisplay } from '@/services/map'
import { MeasurementType } from '@/services/device-measurements'
import { useMeasurementOptions } from '@/composables/widgets/useMeasurementOptions'
import { useWaitingData } from '@/composables/useWaitingData'
import { useStreaming } from '@/composables/useStreaming'
import { NAMESPACES, type RootState } from '@/store'
import { DEVICE_GETTERS, DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { WidgetSettings } from '@/types/dashboard'
import type { WidgetComponentKey } from '@/views/dashboard.vue'

type GetterByDeviceId<T> = (deviceId: string) => T

export default defineComponent({
  name: 'ContinuousMonitoringAndStudyWidget',
  components: {
    BaseWidget,
    StudyContent,
    ContinuousMonitoringContent,
    ContinuousMonitoringAndStudyFooter,
    PopoverToolbar,
    MeasurementOptions,
    LoadingContainer,
    WaitingInitialData
  },
  setup(props) {
    const isLoading = ref(true)
    const store = useStore<RootState>()

    const { isWaitingInitialData, isDeviceConnected, isSmartBridgeConnected } = useWaitingData()
    const {
      openCurrentReadingsStream,
      closeCurrentReadingsStream,
      openDeviceStateStream,
      closeDeviceStateStream
    } = useStreaming()

    const deviceIds = computed(() => props.deviceIds ?? [])
    const settings = computed(() => props.settings)

    const { selectedParameter } = useMeasurementOptions(
      reactive({
        deviceIds: deviceIds,
        settings: settings,
        defaultParameter: MeasurementType.Pm25Raw,
        filterMeasurementStatistics: true,
        passedinParameter: null as number | null
      })
    )

    function getLatestCurrentReadingsAndUpdate(deviceId: string): Promise<void> {
      return store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_LATEST_CURRENT_READINGS_AND_UPDATE}`,
        deviceId
      )
    }

    function getDeviceDetailsAndUpdate(payload: { deviceId: string }): Promise<void> {
      return store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_DEVICE_DETAILS_AND_UPDATE}`,
        payload
      )
    }

    return {
      isLoading,
      isWaitingInitialData,
      isDeviceConnected,
      isSmartBridgeConnected,
      openCurrentReadingsStream,
      closeCurrentReadingsStream,
      openDeviceStateStream,
      closeDeviceStateStream,
      selectedParameter,
      getLatestCurrentReadingsAndUpdate,
      getDeviceDetailsAndUpdate
    }
  },
  props: {
    widgetId: {
      type: String,
      default: undefined
    },
    widgetType: {
      type: String as PropType<WidgetComponentKey>,
      default: undefined
    },
    widgetMaxDevices: {
      type: Object as PropType<Record<WidgetComponentKey, number>>,
      default: undefined
    },
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    settings: {
      type: Object as PropType<WidgetSettings>,
      default: undefined
    },
    isDialog: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'removeWidget', 'refreshDashboard'],
  async mounted() {
    await this.wrapLoading(async () => {
      await this.openCurrentReadingsStream(this.deviceId)
      await this.openDeviceStateStream(this.deviceId)
    })()
  },
  unmounted() {
    this.closeCurrentReadingsStream()
    this.closeDeviceStateStream()
  },
  computed: {
    deviceId(): string {
      return this.deviceIds?.[0] ?? ''
    },
    name(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    model(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    hasLatestStudy(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.HAS_LATEST_STUDY_BY_DEVICE_ID}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    deviceType(): string {
      const modelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
      ](this.model)
      return deviceModelDisplay(modelKey, this.$t.bind(this))
    }
  },
  methods: {
    wrapLoading(f: (...args: unknown[]) => Promise<void>): (...args: unknown[]) => Promise<void> {
      if (typeof f !== 'function') return () => Promise.resolve()
      return async (...args: unknown[]) => {
        this.isLoading = true
        await f(...args)
        this.isLoading = false
      }
    },
    close(): void {
      this.$emit('close')
    },
    removeWidget(options: unknown): void {
      this.$emit('removeWidget', options)
    },
    refreshDashboard(): void {
      this.$emit('refreshDashboard')
    },
    async refresh(): Promise<void> {
      await this.wrapLoading(async () => {
        await this.getLatestCurrentReadingsAndUpdate(this.deviceId)
        await this.getDeviceDetailsAndUpdate({ deviceId: this.deviceId })
      })()
    },
    parameterChange(newValue: number | null): void {
      this.selectedParameter = newValue
    }
  }
})
</script>

<style lang="scss" scoped>
.study-widget,
.continuous-monitoring-widget {
  max-height: 467px;

  :deep(.widget-footer-container) {
    padding: 0;
  }
}

:deep(.widget-content-container) {
  padding-top: 8px;
}

:deep(.device-conditions-wrapper) {
  position: absolute;
  bottom: 15px;
  right: 0;
  width: 100%;

  .study-widget & {
    right: 4%;
  }
}
</style>
